/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React from "react"
import ImageGallery, { ReactImageGalleryProps } from "react-image-gallery"
import { useMediaQuery } from "react-responsive"
import { RemoveScroll } from "react-remove-scroll"
import { Button, SanityImageType } from "@silversea-ssc/ui-components-react"
import { t } from "@lingui/macro"

import { ResponsiveSanityImage } from "@silversea-ssc/ui-components-react"
import clsx from "clsx"
import { i18n } from "@lingui/core"
import { SanityImage } from "../../app-core/_common/sanity/sanity-types"
import { convertImages } from "./utils"
import SlideshowImage from "./SlideshowImage"
import styles from "./Slideshow.module.scss"

export type SlideshowItemImage = SanityImage
export type SlideshowItem = SlideshowItemImage

type SlideshowVariant = "gray" | "white"

export interface InternalSlideshowItemProps {
    key?: string
    isFullscreen?: boolean
    image?: SanityImageType
    description?: string
    //video?: SanityVideo
    isVideo: boolean
    fullScreenEnabled?: boolean
    imageFit?: "contain" | "cover"
    imageAlt?: string
}

interface SlideshowProps {
    items: SlideshowItem[]
    className?: string
    hideCaption?: boolean
    sizes?: string
    fullscreen?: boolean
    variant?: SlideshowVariant
    imageFit?: "contain" | "cover"
    fluidHeight?: boolean
}

interface SlideshowState {
    isFullscreen: boolean
    currentCaption?: string
    items: InternalSlideshowItemProps[]
}

class Slideshow extends React.Component<SlideshowProps, SlideshowState> {
    //isBrowser: boolean
    _imageGallery: any
    hasNavigation: boolean

    constructor(props: SlideshowProps) {
        super(props)
        //this.isBrowser = !isSSR()
        this.hasNavigation = props.items?.length > 1
        const items = convertImages(this.props.items, props.hideCaption || false)

        this.state = {
            isFullscreen: false,
            currentCaption: this.captionForIndex(0, items),
            items: items,
        }
    }

    renderChange = (isFullscreen: boolean) => {
        this.setState({ isFullscreen: !!isFullscreen })
    }

    captionForIndex(index: number, items = this.state.items) {
        const item = items[index]
        return item?.description
    }

    onSlide(index: number) {
        this.setState({
            ...this.state,
            currentCaption: this.captionForIndex(index),
        })
    }

    renderImage(item: InternalSlideshowItemProps): React.ReactNode {
        const { isFullscreen } = this.state
        //const { isVideo } = item

        // return isVideo ? (
        //     <SlideshowVideo
        //         {...item}
        //         isFullscreen={isFullscreen}
        //         fullScreenEnabled={this.props.fullscreen}
        //         imageFit={this.props.imageFit}
        //     />
        // ) : (
        //     <SlideshowImage {...item} isFullscreen={isFullscreen} imageFit={this.props.imageFit} />
        // )

        const { key, ...restItem } = item
        return <SlideshowImage key={key} {...restItem} isFullscreen={isFullscreen} imageFit={this.props.imageFit} />
    }

    renderFullscreenButton: ReactImageGalleryProps["renderFullscreenButton"] = (onClick, isFullscreen) => {
        return !isFullscreen ? (
            <Button
                renderAs="button"
                className={styles.buttonFullscreen}
                onClick={onClick}
                iconName="expand"
                variant="secondary"
                dataContentPiece="fullscreen_button"
                aria-label={`Fullscreen`}
            ></Button>
        ) : (
            <Button
                renderAs="button"
                className={styles.buttonFullscreenClose}
                onClick={onClick}
                iconName="close"
                variant="secondary"
                dataContentPiece="close_button"
                aria-label={`Close`}
            ></Button>
        )
    }

    renderLeftNav: ReactImageGalleryProps["renderLeftNav"] = (onClick, disabled) => {
        return (
            <Button
                renderAs="button"
                className={clsx([styles.arrowButton, styles.arrowButtonLeft])}
                disabled={disabled}
                onClick={onClick}
                iconName="chevron-left"
                variant="secondary"
                dataContentPiece="slideshow_prev_button"
                aria-label={t`Previous`}
            ></Button>
        )
    }

    renderRightNav: ReactImageGalleryProps["renderRightNav"] = (onClick, disabled) => {
        return (
            <Button
                renderAs="button"
                className={clsx([styles.arrowButton, styles.arrowButtonRight])}
                disabled={disabled}
                onClick={onClick}
                iconName="chevron-right"
                variant="secondary"
                dataContentPiece="slideshow_next_button"
                aria-label={t`Next`}
            ></Button>
        )
    }

    renderThumbInner = (item: InternalSlideshowItemProps) => {
        return (
            <div className={styles.imageGalleryThumbnailInner}>
                <ResponsiveSanityImage
                    image={item.image}
                    alt={item.imageAlt}
                    title={item.imageAlt}
                    draggable={false}
                    className="image-gallery-thumbnail-image"
                    defaultWidth={105}
                    defaultHeight={70}
                />
                {/* {item.isVideo && <Icon name="play" className={styles.thumbnailVideoIcon} size="2x" />} */}
            </div>
        )
    }

    render() {
        const {
            className,
            hideCaption = false,
            fullscreen: enableFullscreen,
            imageFit = "cover",
            fluidHeight = false,
        } = this.props

        const showCaption = hideCaption != true || this.hasNavigation

        const imageGalleryProps = {
            ref: (i: any) => (this._imageGallery = i),
            items: this.state.items,
            showIndex: true,
            showNav: true,
            lazyLoad: true,
            renderThumbInner: this.renderThumbInner,
            showThumbnails: this.state.isFullscreen && enableFullscreen,
            showPlayButton: false,
            onScreenChange: this.renderChange,
            showFullscreenButton: enableFullscreen,
            renderFullscreenButton: this.renderFullscreenButton,
            renderLeftNav: this.renderLeftNav,
            renderRightNav: this.renderRightNav,
            renderItem: this.renderImage.bind(this),
            indexSeparator: ` ${i18n._(t({ id: "count-separator-xx-of-xx" }))} `,
            onSlide: this.onSlide.bind(this),
            onClick: () => {
                if (enableFullscreen) this._imageGallery.fullScreen()
            },
        } as unknown as ReactImageGalleryProps

        const GalleyComponent = ImageGallery as any // Hack React 18
        const gallery = <GalleyComponent {...imageGalleryProps} />

        return (
            <div
                className={clsx(styles.slideshow, className, {
                    [styles.slideshowFullscreen]: this.state.isFullscreen,
                    [styles.slideshowHideNavigation]: !this.hasNavigation,
                    [styles.slideshowHideCaption]: !showCaption,
                    [styles.variantGray]: this.props.variant == "gray",
                    [styles.imageFitCover]: imageFit == "cover",
                    [styles.imageFitContain]: imageFit == "contain",
                    [styles.fluidHeight]: fluidHeight,
                })}
            >
                <RemoveScroll enabled={this.state.isFullscreen && enableFullscreen}>
                    {gallery}
                    <div className={styles.imageCaption}>{this.state.currentCaption}</div>
                </RemoveScroll>
            </div>
        )
    }
}

export const ResponsiveSlideshow: React.FC<React.PropsWithChildren<SlideshowProps>> = ({
    fullscreen = true,
    ...props
}) => {
    const isTabletOrDesktop = useMediaQuery({
        query: "(min-width: 768px) AND (min-height: 600px)",
    })

    const responsiveProps = {
        ...props,
        fullscreen: fullscreen && isTabletOrDesktop,
    }
    return <Slideshow {...responsiveProps} />
}
